import {
  EditProjectDto,
  KnownProjectDtoProjectType,
  KnownProjectDtoDisplayWorkflowTypesItem,
  CreateProjectDto,
  KnownProjectFeature
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { CronConverter } from './cron-converter';
import { FormBoolean, ProjectSettingsForm } from './form';

export class ProjectSettingsFormOutput {
  name?: string;
  type?: KnownProjectDtoProjectType;
  parent?: string;
  shortcode?: string;
  senderID?: string;
  image?: string;
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  postcode?: string;
  state?: string;
  country?: string;
  timezone?: string;
  isManagedByAc?: boolean;
  displayWorkflowTypes?: KnownProjectDtoDisplayWorkflowTypesItem[];
  enabledFeatures?: KnownProjectFeature[];
  allowPersonalInfoInReports?: boolean;
  // Not sent to either DTO. Carried so the schema builder can read it —
  // displayWorkflowTypes does not survive the FormOutput round trip.
  showOrders?: FormBoolean;
  overduePeriod?: number;
  reminderPeriod?: number;
  reminderSchedule?: string[];

  constructor(form: ProjectSettingsForm) {
    let displayWorkflowTypes: KnownProjectDtoDisplayWorkflowTypesItem[] = [];
    if (form.showDeliveries === 'yes') {
      displayWorkflowTypes.push(KnownProjectDtoDisplayWorkflowTypesItem.WorkflowDelivery);
    }
    if (form.showOrders === 'yes') {
      displayWorkflowTypes.push(KnownProjectDtoDisplayWorkflowTypesItem.WorkflowRetail);
    }

    if (form.name) this.name = form.name;
    if (form.type) this.type = form.type;
    if (form.parent) this.parent = form.parent;
    if (form.shortcode) this.shortcode = form.shortcode;
    if (form.senderID) this.senderID = form.senderID;
    if (form.image) this.image = form.image;
    if (form.addressLine1) this.addressLine1 = form.addressLine1;
    if (form.addressLine2) this.addressLine2 = form.addressLine2;
    if (form.suburb) this.suburb = form.suburb;
    if (form.postcode) this.postcode = form.postcode;
    if (form.state) this.state = form.state;
    if (form.country) this.country = form.country;
    if (form.timezone) this.timezone = form.timezone;
    if (form.isManagedByAc) this.isManagedByAc = form.isManagedByAc;
    if (displayWorkflowTypes.length > 0) this.displayWorkflowTypes = displayWorkflowTypes;
    // Only set when the control is on the form (experimental users); undefined leaves features unchanged.
    // The list replaces the project's features wholesale, so the ones this form does not control are kept.
    if (form.enableInventoryBookingFlow !== undefined) {
      const otherFeatures = (form.enabledFeatures ?? []).filter(
        (feature) => feature !== KnownProjectFeature.InventoryBookingFlow
      );
      this.enabledFeatures =
        form.enableInventoryBookingFlow === 'yes'
          ? [...otherFeatures, KnownProjectFeature.InventoryBookingFlow]
          : otherFeatures;
    }
    if (form.showOrders !== undefined) this.showOrders = form.showOrders;
    if (form.allowPersonalInfo !== undefined) {
      // Forced off unless Orders are shown, mirroring the field's visibility.
      this.allowPersonalInfoInReports = form.showOrders === 'yes' && form.allowPersonalInfo === 'yes';
    }
    if (form.overduePeriod) this.overduePeriod = ProjectSettingsForm.ConvertHoursToSeconds(form.overduePeriod);
    if (form.reminderPeriod) this.reminderPeriod = ProjectSettingsForm.ConvertHoursToSeconds(form.reminderPeriod);
    const reminderSchedule = CronConverter.GetCronSchedulesFromForm(form);
    if (reminderSchedule.length > 0) this.reminderSchedule = reminderSchedule;
  }

  static ToEditProjectDto(output: ProjectSettingsFormOutput): EditProjectDto {
    return {
      name: output.name,
      projectType: output.type,
      parentID: output.parent,
      shortcode: output.shortcode,
      senderID: output.senderID,
      imageURL: output.image,
      address: {
        line1: output.addressLine1,
        line2: output.addressLine2,
        suburb: output.suburb,
        postcode: output.postcode,
        state: output.state,
        country: output.country
      },
      timezone: output.timezone,
      displayWorkflowTypes: output.displayWorkflowTypes,
      enabledFeatures: output.enabledFeatures,
      allowPersonalInfoInReports: output.allowPersonalInfoInReports,
      overduePeriod: output.overduePeriod,
      minDelayBeforeFirstReminder: output.reminderPeriod,
      reminderSchedule: output.reminderSchedule
    };
  }

  static ToCreateProjectDto(output: ProjectSettingsFormOutput): CreateProjectDto {
    return {
      // We can assert these properties are defined because they are required in the form
      name: output.name!,
      projectType: output.type!,
      parentID: output.parent,
      shortcode: output.shortcode,
      senderID: output.senderID,
      imageURL: output.image,
      address: {
        line1: output.addressLine1,
        line2: output.addressLine2,
        suburb: output.suburb,
        postcode: output.postcode,
        state: output.state,
        country: output.country
      },
      timezone: output.timezone,
      displayWorkflowTypes: output.displayWorkflowTypes,
      enabledFeatures: output.enabledFeatures,
      allowPersonalInfoInReports: output.allowPersonalInfoInReports,
      overduePeriod: output.overduePeriod,
      minDelayBeforeFirstNotification: output.reminderPeriod,
      reminderSchedule: output.reminderSchedule
    };
  }
}
